/**
 * Admin Health Panel — at /admin/health.
 *
 * Reads `/api/admin/health` and renders the technical state of the system:
 * services (DB, S3, SMTP, third-party APIs), workers, the publishing pipeline,
 * Link in Bio, public API, blog, newsletter and the host process.
 *
 * Refreshes every 30 seconds so the admin can leave it open and detect issues
 * as they happen.
 */
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Cpu,
  Database,
  Globe,
  HardDrive,
  Heart,
  Inbox,
  Key,
  Loader2,
  Mail,
  Newspaper,
  RefreshCw,
  Server,
  Sparkles,
  Users as UsersIcon,
  Workflow,
  XCircle,
  Zap,
} from 'lucide-react';
import { api } from '../lib/api';

/* ── Types matching the backend response ──────────────────── */
type ServiceCheck = {
  ok: boolean;
  latencyMs: number | null;
  error: string | null;
  note?: string;
};

type WorkerHealth = {
  name: string;
  enabled: boolean;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  lastErrorAt: string | null;
  lastError: string | null;
  lastDurationMs: number | null;
  runs: number;
  errors: number;
  disabledReason: string | null;
};

type HealthResponse = {
  services: Record<string, ServiceCheck>;
  workers: WorkerHealth[];
  posts: {
    scheduledNext24h: number;
    publishingStuck: number;
    failed24h: number;
    partialFailed24h: number;
    retriesPending: number;
    upcoming: { id: string; caption: string; scheduledAt: string | null; userEmail: string }[];
    recentFailures: {
      id: string;
      caption: string;
      status: string;
      error: string;
      updatedAt: string | null;
      userEmail: string;
    }[];
  };
  social: { connected: number; disconnected: number };
  bio: {
    pagesPublished: number;
    pagesDraft: number;
    totalViews: number;
    totalClicks: number;
    subscribers24h: number;
  };
  api: { activeTokens: number; usedTokens24h: number };
  blog: {
    published: number;
    scheduled: number;
    ideasPending: number;
    nextScheduledAt: string | null;
    nextScheduledTitle: string | null;
  };
  email: {
    smtpConfigured: boolean;
    activeSubscribers: number;
    lastDigest: {
      digestDate: string;
      articleCount: number;
      recipientCount: number;
      createdAt: string | null;
    } | null;
  };
  users: { total: number };
  system: {
    uptimeSeconds: number;
    startedAt: string;
    now: string;
    nodeVersion: string;
    env: string;
    memory: { rssMb: number; heapUsedMb: number; heapTotalMb: number };
    loadAvg: number[];
    platform: string;
    pid: number;
  };
};

/* ── Friendly labels & helpers ────────────────────────────── */
const SERVICE_LABELS: Record<string, { label: string; icon: typeof Database }> = {
  database: { label: 'Postgres', icon: Database },
  s3: { label: 'AWS S3', icon: HardDrive },
  smtp: { label: 'SMTP', icon: Mail },
  postforme: { label: 'Postforme', icon: Zap },
  openai: { label: 'OpenAI', icon: Sparkles },
  mollie: { label: 'Mollie', icon: Inbox },
  google_oauth: { label: 'Google OAuth', icon: Globe },
};

const WORKER_LABELS: Record<string, string> = {
  publishWorker: 'Publish (posts → redes)',
  autoBlogWorker: 'Autoblog (IA → blog)',
  newsletterWorker: 'Newsletter (digest diario)',
  inspirationWorker: 'Inspiración (galería diaria)',
};

const WORKER_INTERVAL_SECONDS: Record<string, number> = {
  publishWorker: 30,
  autoBlogWorker: 60,
  newsletterWorker: 60,
  inspirationWorker: 5 * 60,
};

function relative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const s = Math.round(diffMs / 1000);
  if (s < 0) return `en ${Math.abs(s)} s`;
  if (s < 60) return `hace ${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `hace ${m} min`;
  const h = Math.round(m / 60);
  if (h < 48) return `hace ${h} h`;
  const d = Math.round(h / 24);
  return `hace ${d} d`;
}

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ${seconds % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

/* ── Reusable presentational primitives ───────────────────── */
function StatusDot({ ok, idle }: { ok: boolean; idle?: boolean }) {
  if (idle) return <span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-300" />;
  return (
    <span
      className={
        'inline-block h-2.5 w-2.5 rounded-full ' +
        (ok ? 'bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.15)]' : 'bg-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.15)]')
      }
    />
  );
}

function Pill({ tone, children }: { tone: 'green' | 'amber' | 'red' | 'slate'; children: React.ReactNode }) {
  const map = {
    green: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    red: 'bg-red-50 text-red-700 ring-red-200',
    slate: 'bg-slate-100 text-slate-600 ring-slate-200',
  } as const;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${map[tone]}`}>
      {children}
    </span>
  );
}

function Kpi({
  label,
  value,
  hint,
  tone = 'slate',
  icon: Icon,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone?: 'slate' | 'amber' | 'red' | 'green';
  icon?: typeof Database;
}) {
  const toneMap = {
    slate: 'text-slate-900',
    amber: 'text-amber-600',
    red: 'text-red-600',
    green: 'text-emerald-600',
  } as const;
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4">
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500 uppercase tracking-wide">
        {Icon ? <Icon size={14} /> : null}
        <span>{label}</span>
      </div>
      <div className={`mt-2 text-2xl font-bold tabular-nums ${toneMap[tone]}`}>{value}</div>
      {hint ? <div className="mt-1 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────── */
export function AdminHealth() {
  const query = useQuery<HealthResponse>({
    queryKey: ['admin', 'health'],
    queryFn: () => api<HealthResponse>('/api/admin/health'),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 text-slate-500">
        <Loader2 className="animate-spin" size={16} />
        Cargando estado del sistema…
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-6 text-red-700">
        <div className="flex items-center gap-2 font-semibold">
          <AlertTriangle size={18} /> No se pudo cargar el estado del sistema.
        </div>
        <p className="mt-2 text-sm">
          {(query.error as Error | undefined)?.message ?? 'Reintenta en unos segundos.'}
        </p>
      </div>
    );
  }

  const h = query.data;
  const servicesArr = Object.entries(h.services);
  const criticalDown = ['database', 's3'].some((k) => h.services[k] && !h.services[k]!.ok);
  const anyServiceDown = servicesArr.some(([, v]) => v.ok === false && !v.note); // ignore "not configured" notes
  const overallOk = !criticalDown && !anyServiceDown && !h.workers.some((w) => w.enabled && w.errors > 0 && w.lastError && (!w.lastSuccessAt || (w.lastErrorAt && w.lastErrorAt > w.lastSuccessAt)));
  const overallTone = criticalDown ? 'red' : anyServiceDown || h.posts.publishingStuck > 0 || h.posts.failed24h > 0 ? 'amber' : 'green';

  return (
    <div className="space-y-8">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Heart size={22} className="text-brand-600" />
            Panel de salud técnico
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Estado en tiempo real de los servicios, workers y la pipeline de publicación.
            Refresco automático cada 30 segundos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {overallTone === 'green' && <Pill tone="green"><CheckCircle2 size={12} /> Todo operativo</Pill>}
          {overallTone === 'amber' && <Pill tone="amber"><AlertTriangle size={12} /> Atención</Pill>}
          {overallTone === 'red' && <Pill tone="red"><XCircle size={12} /> Servicios críticos caídos</Pill>}
          <button
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw size={13} className={query.isFetching ? 'animate-spin' : ''} />
            Actualizar
          </button>
        </div>
      </div>

      {/* ── Services ───────────────────────────────────────── */}
      <section>
        <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide flex items-center gap-2">
          <Server size={14} /> Servicios externos
        </h2>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {servicesArr.map(([key, svc]) => {
            const meta = SERVICE_LABELS[key] ?? { label: key, icon: Globe };
            const Icon = meta.icon;
            const notConfigured = svc.note?.includes('no configurado') || svc.note?.includes('no configurada');
            return (
              <div
                key={key}
                className={
                  'rounded-xl border p-4 ' +
                  (svc.ok
                    ? 'bg-white border-slate-200'
                    : notConfigured
                      ? 'bg-slate-50 border-slate-200'
                      : 'bg-red-50/30 border-red-200')
                }
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                    <Icon size={15} className="text-slate-500" />
                    {meta.label}
                  </div>
                  <StatusDot ok={svc.ok} idle={notConfigured} />
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  {svc.latencyMs !== null ? (
                    <span>Latencia: <span className="font-medium text-slate-700">{svc.latencyMs} ms</span></span>
                  ) : svc.note ? (
                    <span>{svc.note}</span>
                  ) : svc.ok ? (
                    <span>OK</span>
                  ) : (
                    <span>—</span>
                  )}
                </div>
                {svc.error ? (
                  <div className="mt-2 text-xs text-red-700 bg-red-50 rounded-md px-2 py-1 break-words">
                    {svc.error}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Posts pipeline ─────────────────────────────────── */}
      <section>
        <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide flex items-center gap-2">
          <Activity size={14} /> Pipeline de publicación
        </h2>
        <div className="mt-3 grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Kpi label="Próximas 24h" value={h.posts.scheduledNext24h} icon={Activity} />
          <Kpi
            label="Bloqueadas en publishing"
            value={h.posts.publishingStuck}
            tone={h.posts.publishingStuck > 0 ? 'red' : 'green'}
            hint=">10 min en estado publishing"
          />
          <Kpi
            label="Fallos 24h"
            value={h.posts.failed24h}
            tone={h.posts.failed24h > 0 ? 'red' : 'green'}
          />
          <Kpi
            label="Fallos parciales 24h"
            value={h.posts.partialFailed24h}
            tone={h.posts.partialFailed24h > 0 ? 'amber' : 'green'}
          />
          <Kpi label="Reintentos pendientes" value={h.posts.retriesPending} icon={RefreshCw} />
        </div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Upcoming */}
          <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700 uppercase tracking-wide">
              Próximas 5 publicaciones programadas
            </div>
            {h.posts.upcoming.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-400 text-center">No hay programaciones próximas.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {h.posts.upcoming.map((p) => (
                  <li key={p.id} className="px-4 py-2.5 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-slate-700 truncate">{p.caption || '(sin caption)'}</span>
                      <span className="text-xs text-slate-400 shrink-0">
                        {p.scheduledAt ? new Date(p.scheduledAt).toLocaleString() : '—'}
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5 truncate">{p.userEmail}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Recent failures */}
          <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700 uppercase tracking-wide">
              Últimos fallos
            </div>
            {h.posts.recentFailures.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-400 text-center">Sin fallos recientes 🎉</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {h.posts.recentFailures.map((p) => (
                  <li key={p.id} className="px-4 py-2.5 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-slate-700 truncate">{p.caption || '(sin caption)'}</span>
                      <Pill tone={p.status === 'failed' ? 'red' : 'amber'}>{p.status}</Pill>
                    </div>
                    <div className="text-xs text-red-700 mt-1 break-words line-clamp-2">{p.error || '—'}</div>
                    <div className="text-xs text-slate-400 mt-0.5 truncate">
                      {p.userEmail} · {relative(p.updatedAt)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* ── Workers ────────────────────────────────────────── */}
      <section>
        <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide flex items-center gap-2">
          <Workflow size={14} /> Workers en background
        </h2>
        <div className="mt-3 rounded-xl bg-white border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-2.5 font-medium">Worker</th>
                <th className="px-4 py-2.5 font-medium">Estado</th>
                <th className="px-4 py-2.5 font-medium">Último tick</th>
                <th className="px-4 py-2.5 font-medium">Último éxito</th>
                <th className="px-4 py-2.5 font-medium text-right">Runs / Errores</th>
                <th className="px-4 py-2.5 font-medium text-right">Duración</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {h.workers.map((w) => {
                const friendly = WORKER_LABELS[w.name] ?? w.name;
                const expectedSeconds = WORKER_INTERVAL_SECONDS[w.name] ?? 60;
                const lastRunMs = w.lastRunAt ? Date.now() - new Date(w.lastRunAt).getTime() : Infinity;
                // Workers that gate on Madrid-hour (newsletter) or 5-min cadence have lenient slack
                const slack = expectedSeconds === 30 ? 3 : 3;
                const stale = w.enabled && (!w.lastRunAt || lastRunMs > expectedSeconds * 1000 * slack);
                const lastFailure = w.lastErrorAt && w.lastSuccessAt
                  ? new Date(w.lastErrorAt).getTime() > new Date(w.lastSuccessAt).getTime()
                  : Boolean(w.lastErrorAt) && !w.lastSuccessAt;
                const tone: 'green' | 'amber' | 'red' | 'slate' = !w.enabled
                  ? 'slate'
                  : lastFailure
                    ? 'red'
                    : stale
                      ? 'amber'
                      : 'green';
                const label = !w.enabled
                  ? 'Desactivado'
                  : lastFailure
                    ? 'Último intento falló'
                    : stale
                      ? 'Posiblemente atascado'
                      : 'Funcionando';
                return (
                  <tr key={w.name}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{friendly}</div>
                      <div className="text-xs text-slate-400">{w.name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Pill tone={tone}>
                        <StatusDot ok={tone === 'green'} idle={tone === 'slate'} />
                        {label}
                      </Pill>
                      {w.disabledReason ? (
                        <div className="text-xs text-slate-400 mt-1">{w.disabledReason}</div>
                      ) : null}
                      {w.lastError && lastFailure ? (
                        <div className="text-xs text-red-700 mt-1 break-words line-clamp-2 max-w-md">
                          {w.lastError}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{relative(w.lastRunAt)}</td>
                    <td className="px-4 py-3 text-slate-600">{relative(w.lastSuccessAt)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <span className="text-slate-700">{w.runs}</span>
                      <span className="text-slate-300"> / </span>
                      <span className={w.errors > 0 ? 'text-red-600 font-semibold' : 'text-slate-400'}>
                        {w.errors}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600 tabular-nums">
                      {w.lastDurationMs !== null ? `${w.lastDurationMs} ms` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Domain KPIs ────────────────────────────────────── */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Bio */}
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Globe size={15} className="text-brand-600" /> Link in Bio
            </h3>
            <Pill tone="slate">zlink.club</Pill>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <Kpi label="Publicadas" value={h.bio.pagesPublished} tone="green" />
            <Kpi label="Borrador" value={h.bio.pagesDraft} />
            <Kpi label="Suscriptores 24h" value={h.bio.subscribers24h} />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Kpi label="Vistas totales" value={h.bio.totalViews.toLocaleString()} />
            <Kpi label="Clicks totales" value={h.bio.totalClicks.toLocaleString()} />
          </div>
        </div>

        {/* API */}
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Key size={15} className="text-brand-600" /> API pública
            </h3>
            <Pill tone="slate">/api/v1</Pill>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Kpi label="Tokens activos" value={h.api.activeTokens} />
            <Kpi label="Usados últ. 24h" value={h.api.usedTokens24h} tone="green" />
          </div>
        </div>

        {/* Blog */}
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Newspaper size={15} className="text-brand-600" /> Blog
            </h3>
            {h.blog.nextScheduledAt ? (
              <Pill tone="slate">Próximo: {new Date(h.blog.nextScheduledAt).toLocaleString()}</Pill>
            ) : null}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <Kpi label="Publicados" value={h.blog.published} tone="green" />
            <Kpi label="Programados" value={h.blog.scheduled} />
            <Kpi label="Ideas pendientes" value={h.blog.ideasPending} />
          </div>
          {h.blog.nextScheduledTitle ? (
            <div className="mt-3 text-xs text-slate-500 truncate">
              <span className="text-slate-400">Próximo título:</span> {h.blog.nextScheduledTitle}
            </div>
          ) : null}
        </div>

        {/* Email / Newsletter */}
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Mail size={15} className="text-brand-600" /> Email & Newsletter
            </h3>
            <Pill tone={h.email.smtpConfigured ? 'green' : 'amber'}>
              {h.email.smtpConfigured ? 'SMTP configurado' : 'SMTP sin configurar'}
            </Pill>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Kpi label="Suscriptores activos" value={h.email.activeSubscribers} />
            <Kpi
              label="Último digest"
              value={h.email.lastDigest ? h.email.lastDigest.digestDate : '—'}
              hint={
                h.email.lastDigest
                  ? `${h.email.lastDigest.recipientCount} destinatarios · ${h.email.lastDigest.articleCount} artículos`
                  : 'Aún no se ha enviado ningún digest'
              }
            />
          </div>
        </div>
      </section>

      {/* ── Other counters ─────────────────────────────────── */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Usuarios totales" value={h.users.total} icon={UsersIcon} />
        <Kpi
          label="Cuentas sociales conectadas"
          value={h.social.connected}
          hint={h.social.disconnected > 0 ? `${h.social.disconnected} desconectadas` : undefined}
          tone="green"
        />
        <Kpi label="Carga 1m" value={h.system.loadAvg[0] ?? '—'} icon={Cpu} />
        <Kpi
          label="Memoria RSS"
          value={`${h.system.memory.rssMb} MB`}
          hint={`heap ${h.system.memory.heapUsedMb} / ${h.system.memory.heapTotalMb} MB`}
        />
      </section>

      {/* ── System footer ──────────────────────────────────── */}
      <section className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs text-slate-500 flex flex-wrap gap-x-6 gap-y-2">
        <span>
          <span className="text-slate-400">Uptime:</span>{' '}
          <span className="font-medium text-slate-700">{formatUptime(h.system.uptimeSeconds)}</span>
        </span>
        <span>
          <span className="text-slate-400">Arranque:</span>{' '}
          {new Date(h.system.startedAt).toLocaleString()}
        </span>
        <span>
          <span className="text-slate-400">Node:</span> {h.system.nodeVersion}
        </span>
        <span>
          <span className="text-slate-400">PID:</span> {h.system.pid}
        </span>
        <span>
          <span className="text-slate-400">Env:</span> {h.system.env}
        </span>
        <span>
          <span className="text-slate-400">Host:</span> {h.system.platform}
        </span>
      </section>
    </div>
  );
}
